import { useState } from 'react';
import { createPortal } from 'react-dom';
import { 
    useAdminRedeemCodes, 
    useAdminRedeemActions, 
    useRedeemAnalytics 
} from '@hooks/useRedeem';
import { useVIPPackages } from '@hooks/usePayment';
import './RedeemManager.css';

function RedeemManager() {
    const { codes, isLoading, mutate, error } = useAdminRedeemCodes();
    const { createCode, updateCode, deleteCode, loading: actionLoading } = useAdminRedeemActions();
    const { analytics, isLoading: analyticsLoading } = useRedeemAnalytics();
    const { packages } = useVIPPackages();

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showAnalytics, setShowAnalytics] = useState(false);
    const [editingCode, setEditingCode] = useState(null);
    const [filter, setFilter] = useState('all'); // all, active, inactive, expired

    console.log('[RedeemManager] Render state:', { 
        codesLength: codes?.length, 
        isLoading, 
        hasError: !!error,
        error: error?.message 
    });

    // Form state
    const [formData, setFormData] = useState({
        code: '',
        package_code: '',
        description: '',
        max_uses: '',
        valid_until: ''
    });
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');

    // Debug: Test connection
    const testConnection = async () => {
        const adminToken = localStorage.getItem('adminToken');
        console.log('=== CONNECTION TEST ===');
        console.log('Admin token exists:', !!adminToken);
        console.log('Admin token (first 20 chars):', adminToken?.substring(0, 20));
        console.log('Codes array:', codes);
        console.log('Is loading:', isLoading);
        console.log('Has error:', error);
        
        // Test database
        try {
            const response = await fetch('https://dinoshortdb.fun/api/test-database');
            const result = await response.json();
            console.log('=== DATABASE TEST ===');
            console.log(result);
            
            if (result.success) {
                const tables = result.results.tables;
                alert(
                    `Database Test:\n\n` +
                    `Supabase: ${result.results.supabase_initialized ? '✅' : '❌'}\n` +
                    `redeem_codes: ${tables.redeem_codes?.exists ? `✅ (${tables.redeem_codes.sample_count} rows)` : `❌ ${tables.redeem_codes?.error}`}\n` +
                    `redeem_history: ${tables.redeem_history?.exists ? '✅' : '❌'}\n` +
                    `vip_packages: ${tables.vip_packages?.exists ? '✅' : '❌'}\n\n` +
                    `Check console for details`
                );
            } else {
                alert(`Database Test FAILED:\n${result.error}\n\nCheck console for details`);
            }
        } catch (e) {
            console.error('Database test failed:', e);
            alert('Database test failed. Check console for details.');
        }
    };

    const handleCreateCode = async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        console.log('[RedeemManager] Creating code with data:', formData);

        if (!formData.code || !formData.package_code) {
            setFormError('Code and package are required');
            return;
        }

        const payload = {
            code: formData.code.trim(),
            package_code: formData.package_code,
            description: formData.description.trim() || null,
            max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
            valid_until: formData.valid_until || null
        };

        console.log('[RedeemManager] Payload:', payload);

        const result = await createCode(payload);

        console.log('[RedeemManager] Create result:', result);

        if (result.success) {
            setFormSuccess('Code created successfully!');
            setFormData({ code: '', package_code: '', description: '', max_uses: '', valid_until: '' });
            mutate();
            setTimeout(() => {
                setShowCreateModal(false);
                setFormSuccess('');
            }, 1500);
        } else {
            setFormError(result.error);
        }
    };

    const handleToggleActive = async (id, currentStatus) => {
        const result = await updateCode(id, { is_active: !currentStatus });
        if (result.success) {
            mutate();
        }
    };

    const handleDeleteCode = async (id) => {
        if (!confirm('Are you sure you want to delete this code? This action cannot be undone.')) {
            return;
        }

        const result = await deleteCode(id);
        if (result.success) {
            mutate();
        }
    };

    const handleEditMaxUses = async (id) => {
        const newMaxUses = prompt('Enter new max uses (leave empty for unlimited):');
        if (newMaxUses === null) return;

        const result = await updateCode(id, { 
            max_uses: newMaxUses === '' ? null : parseInt(newMaxUses) 
        });
        
        if (result.success) {
            mutate();
        }
    };

    const getFilteredCodes = () => {
        if (!codes) return [];

        return codes.filter(code => {
            if (filter === 'active') return code.is_active;
            if (filter === 'inactive') return !code.is_active;
            if (filter === 'expired') return code.valid_until && new Date(code.valid_until) < new Date();
            return true; // all
        });
    };

    const getCodeStatus = (code) => {
        if (!code.is_active) return { label: 'Disabled', class: 'status-disabled' };
        if (code.valid_until && new Date(code.valid_until) < new Date()) {
            return { label: 'Expired', class: 'status-expired' };
        }
        if (code.max_uses !== null && code.current_uses >= code.max_uses) {
            return { label: 'Used Up', class: 'status-used' };
        }
        return { label: 'Active', class: 'status-active' };
    };

    const filteredCodes = getFilteredCodes();

    return (
        <div className="redeem-manager">
            {/* Analytics Section */}
            {showAnalytics && (
                <div className="analytics-section">
                    <div className="section-header">
                        <h3>📊 Analytics</h3>
                        <button 
                            className="close-btn"
                            onClick={() => setShowAnalytics(false)}
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                    {analyticsLoading ? (
                        <div className="loading">Loading analytics...</div>
                    ) : (
                        <div className="analytics-grid">
                            <div className="analytics-card">
                                <div className="analytics-icon">🎟️</div>
                                <div className="analytics-value">{analytics.total_codes || 0}</div>
                                <div className="analytics-label">Total Codes</div>
                            </div>
                            <div className="analytics-card">
                                <div className="analytics-icon">✅</div>
                                <div className="analytics-value">{analytics.active_codes || 0}</div>
                                <div className="analytics-label">Active Codes</div>
                            </div>
                            <div className="analytics-card">
                                <div className="analytics-icon">🎁</div>
                                <div className="analytics-value">{analytics.successful_redeems || 0}</div>
                                <div className="analytics-label">Total Redeems</div>
                            </div>
                            <div className="analytics-card">
                                <div className="analytics-icon">📈</div>
                                <div className="analytics-value">{analytics.success_rate || '0%'}</div>
                                <div className="analytics-label">Success Rate</div>
                            </div>
                            <div className="analytics-card">
                                <div className="analytics-icon">🔥</div>
                                <div className="analytics-value">{analytics.recent_redeems_7d || 0}</div>
                                <div className="analytics-label">Last 7 Days</div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Header Actions */}
            <div className="manager-header">
                <h2>Redeem Code Manager</h2>
                <div className="header-actions">
                    <button 
                        className="admin-btn admin-btn-warning"
                        onClick={testConnection}
                        title="Test connection & debug"
                    >
                        <i className="fas fa-bug"></i> Debug
                    </button>
                    <button 
                        className="admin-btn analytics-btn"
                        onClick={() => setShowAnalytics(!showAnalytics)}
                        disabled={analyticsLoading}
                    >
                        <i className="fas fa-chart-bar"></i>
                        {showAnalytics ? 'Hide' : 'Analytics'}
                    </button>
                    <button 
                        className="admin-btn create-btn"
                        onClick={() => {
                            console.log('[RedeemManager] ✅ Create button clicked!');
                            setShowCreateModal(true);
                        }}
                        disabled={isLoading || actionLoading}
                    >
                        <i className="fas fa-plus"></i> Create Code
                    </button>
                    <button 
                        className="admin-btn refresh-btn"
                        onClick={() => {
                            console.log('[RedeemManager] ✅ Refresh button clicked!');
                            mutate();
                        }}
                        disabled={isLoading}
                    >
                        <i className={`fas fa-sync ${isLoading ? 'fa-spin' : ''}`}></i> Refresh
                    </button>
                </div>
            </div>

            {/* Filter Tabs */}
            <div className="filter-tabs">
                <button 
                    className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                    onClick={() => setFilter('all')}
                >
                    All ({codes?.length || 0})
                </button>
                <button 
                    className={`filter-tab ${filter === 'active' ? 'active' : ''}`}
                    onClick={() => setFilter('active')}
                >
                    Active ({codes?.filter(c => c.is_active).length || 0})
                </button>
                <button 
                    className={`filter-tab ${filter === 'inactive' ? 'active' : ''}`}
                    onClick={() => setFilter('inactive')}
                >
                    Disabled ({codes?.filter(c => !c.is_active).length || 0})
                </button>
                <button 
                    className={`filter-tab ${filter === 'expired' ? 'active' : ''}`}
                    onClick={() => setFilter('expired')}
                >
                    Expired ({codes?.filter(c => c.valid_until && new Date(c.valid_until) < new Date()).length || 0})
                </button>
            </div>

            {/* Codes Table */}
            <div className="codes-table-container">
                {error && (
                    <div className="error-message" style={{ margin: '1rem', padding: '1rem', background: 'rgba(255,82,82,0.2)', borderRadius: '8px', color: '#ff5252' }}>
                        <strong>Error loading codes:</strong> {error.message || String(error)}
                        <br />
                        <small>Check console for details. Make sure you're logged in as admin.</small>
                    </div>
                )}
                {isLoading ? (
                    <div className="loading">Loading codes...</div>
                ) : filteredCodes.length === 0 ? (
                    <div className="no-data">
                        <i className="fas fa-inbox"></i>
                        <p>No codes found</p>
                        {!error && codes && codes.length > 0 && (
                            <small style={{ color: '#999', marginTop: '0.5rem' }}>
                                {codes.length} code(s) in database, but filtered out
                            </small>
                        )}
                    </div>
                ) : (
                    <table className="codes-table">
                        <thead>
                            <tr>
                                <th>Code</th>
                                <th>Package</th>
                                <th>Usage</th>
                                <th>Expires</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCodes.map((code) => {
                                const status = getCodeStatus(code);
                                return (
                                    <tr key={code.id}>
                                        <td>
                                            <div className="code-cell">
                                                <strong>{code.code}</strong>
                                                {code.description && (
                                                    <small>{code.description}</small>
                                                )}
                                            </div>
                                        </td>
                                        <td>{code.vip_packages?.package_name || code.package_code}</td>
                                        <td>
                                            <div className="usage-cell">
                                                <span className="usage-text">
                                                    {code.current_uses} / {code.max_uses === null ? '∞' : code.max_uses}
                                                </span>
                                                <button 
                                                    className="edit-usage-btn"
                                                    onClick={() => handleEditMaxUses(code.id)}
                                                    title="Edit max uses"
                                                >
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                            </div>
                                        </td>
                                        <td>
                                            {code.valid_until 
                                                ? new Date(code.valid_until).toLocaleDateString() 
                                                : 'Never'}
                                        </td>
                                        <td>
                                            <span className={`status-badge ${status.class}`}>
                                                {status.label}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                <button
                                                    className={`action-btn ${code.is_active ? 'disable-btn' : 'enable-btn'}`}
                                                    onClick={() => handleToggleActive(code.id, code.is_active)}
                                                    disabled={actionLoading}
                                                    title={code.is_active ? 'Disable' : 'Enable'}
                                                >
                                                    <i className={`fas fa-${code.is_active ? 'ban' : 'check'}`}></i>
                                                </button>
                                                <button
                                                    className="action-btn delete-btn"
                                                    onClick={() => handleDeleteCode(code.id)}
                                                    disabled={actionLoading}
                                                    title="Delete"
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Create Modal - Render via Portal to body */}
            {showCreateModal && createPortal(
                <div 
                    className="redeem-modal-overlay" 
                    onClick={() => setShowCreateModal(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0, 0, 0, 0.98)',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        zIndex: 2147483647, /* Max z-index */
                        padding: '1rem',
                        paddingTop: '100px',
                        overflowY: 'auto'
                    }}
                >
                    <div 
                        className="redeem-modal-content" 
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
                            border: '2px solid rgba(255, 215, 0, 0.3)',
                            borderRadius: '20px',
                            width: '90%',
                            maxWidth: '550px',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.8), 0 0 80px rgba(255,215,0,0.1)'
                        }}
                    >
                        <div className="modal-header" style={{
                            padding: '1.5rem',
                            borderBottom: '1px solid rgba(255,215,0,0.2)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h3 style={{
                                margin: 0,
                                background: 'linear-gradient(135deg, #FFD700, #FFC107)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                fontSize: '1.5rem',
                                fontWeight: 800
                            }}>✨ Create New Redeem Code</h3>
                            <button 
                                onClick={() => setShowCreateModal(false)}
                                style={{
                                    background: 'rgba(255,255,255,0.1)',
                                    border: 'none',
                                    color: '#fff',
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '10px',
                                    cursor: 'pointer',
                                    fontSize: '1.2rem'
                                }}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <form onSubmit={handleCreateCode} style={{ padding: '1.5rem' }}>
                            {formError && (
                                <div style={{
                                    background: 'rgba(255,82,82,0.2)',
                                    border: '1px solid rgba(255,82,82,0.4)',
                                    color: '#ff5252',
                                    padding: '0.75rem 1rem',
                                    borderRadius: '8px',
                                    marginBottom: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <i className="fas fa-exclamation-circle"></i> {formError}
                                </div>
                            )}
                            {formSuccess && (
                                <div style={{
                                    background: 'rgba(76,175,80,0.2)',
                                    border: '1px solid rgba(76,175,80,0.4)',
                                    color: '#4caf50',
                                    padding: '0.75rem 1rem',
                                    borderRadius: '8px',
                                    marginBottom: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    <i className="fas fa-check-circle"></i> {formSuccess}
                                </div>
                            )}

                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={{ display: 'block', color: '#FFD700', marginBottom: '0.5rem', fontWeight: 600 }}>
                                    Code *
                                </label>
                                <input
                                    type="text"
                                    value={formData.code}
                                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                                    placeholder="e.g., VIP2024"
                                    required
                                    disabled={actionLoading}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem 1rem',
                                        background: 'rgba(0,0,0,0.4)',
                                        border: '2px solid rgba(255,215,0,0.2)',
                                        borderRadius: '10px',
                                        color: '#fff',
                                        fontSize: '1rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '2px'
                                    }}
                                />
                                <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
                                    Code will be auto-converted to uppercase
                                </small>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={{ display: 'block', color: '#FFD700', marginBottom: '0.5rem', fontWeight: 600 }}>
                                    VIP Package *
                                </label>
                                <select
                                    value={formData.package_code}
                                    onChange={(e) => setFormData({ ...formData, package_code: e.target.value })}
                                    required
                                    disabled={actionLoading}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem 1rem',
                                        background: 'rgba(0,0,0,0.4)',
                                        border: '2px solid rgba(255,215,0,0.2)',
                                        borderRadius: '10px',
                                        color: '#fff',
                                        fontSize: '1rem'
                                    }}
                                >
                                    <option value="">Select package...</option>
                                    {packages?.map((pkg) => (
                                        <option key={pkg.package_code} value={pkg.package_code}>
                                            {pkg.package_name} 
                                            {pkg.duration_days === null ? ' (Lifetime)' : ` (${pkg.duration_days} days)`}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ marginBottom: '1.25rem' }}>
                                <label style={{ display: 'block', color: 'rgba(255,255,255,0.8)', marginBottom: '0.5rem', fontWeight: 600 }}>
                                    Description
                                </label>
                                <input
                                    type="text"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="e.g., New Year Promo"
                                    disabled={actionLoading}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem 1rem',
                                        background: 'rgba(0,0,0,0.4)',
                                        border: '2px solid rgba(255,215,0,0.2)',
                                        borderRadius: '10px',
                                        color: '#fff',
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                                <div>
                                    <label style={{ display: 'block', color: 'rgba(255,255,255,0.8)', marginBottom: '0.5rem', fontWeight: 600 }}>
                                        Max Uses
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.max_uses}
                                        onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                                        placeholder="∞ unlimited"
                                        min="1"
                                        disabled={actionLoading}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem 1rem',
                                            background: 'rgba(0,0,0,0.4)',
                                            border: '2px solid rgba(255,215,0,0.2)',
                                            borderRadius: '10px',
                                            color: '#fff',
                                            fontSize: '1rem'
                                        }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', color: 'rgba(255,255,255,0.8)', marginBottom: '0.5rem', fontWeight: 600 }}>
                                        Expires At
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={formData.valid_until}
                                        onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                                        disabled={actionLoading}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem 1rem',
                                            background: 'rgba(0,0,0,0.4)',
                                            border: '2px solid rgba(255,215,0,0.2)',
                                            borderRadius: '10px',
                                            color: '#fff',
                                            fontSize: '1rem'
                                        }}
                                    />
                                </div>
                            </div>

                            <div style={{ 
                                display: 'flex', 
                                gap: '1rem', 
                                paddingTop: '1rem',
                                borderTop: '1px solid rgba(255,215,0,0.15)'
                            }}>
                                <button 
                                    type="button" 
                                    onClick={() => setShowCreateModal(false)}
                                    disabled={actionLoading}
                                    style={{
                                        flex: 1,
                                        padding: '0.875rem 1.5rem',
                                        background: 'rgba(0,0,0,0.4)',
                                        border: '1px solid rgba(255,215,0,0.2)',
                                        borderRadius: '10px',
                                        color: 'rgba(255,255,255,0.8)',
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={actionLoading}
                                    style={{
                                        flex: 1,
                                        padding: '0.875rem 1.5rem',
                                        background: 'linear-gradient(135deg, #FFD700 0%, #FFC107 50%, #B8860B 100%)',
                                        border: 'none',
                                        borderRadius: '10px',
                                        color: '#000',
                                        fontSize: '1rem',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 15px rgba(255,215,0,0.3)'
                                    }}
                                >
                                    {actionLoading ? (
                                        <><i className="fas fa-spinner fa-spin"></i> Creating...</>
                                    ) : (
                                        <><i className="fas fa-plus"></i> Create Code</>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

export default RedeemManager;

